import type {DropdownOptionProps} from '@wix/wix-ui-tpa/cssVars';

export type SubdivisionsData = {
  options: DropdownOptionProps[];
  label: string;
};

export type CountriesData = {
  countries: DropdownOptionProps[];
  getSubdivisionsForCountry: (countryShortKey: string) => SubdivisionsData | undefined;
};

export const excludedCountryShortKeys = new Set(['IR', 'SY', 'CU', 'KP']);

export async function loadCountriesData(language: string): Promise<CountriesData> {
  const [{getAllCountries}, translations] = await Promise.all([
    import('@wix/locale-dataset-javascript'),
    import(`@wix/locale-dataset-data/resources/translations/messages_${language}.json`).catch(
      () => import('@wix/locale-dataset-data/resources/translations/messages_en.json')
    ),
  ]);

  const translationMap = translations as Record<string, string>;
  const translate = (key: string) => translationMap[key] ?? key;

  const allCountries = getAllCountries().filter((c) => !excludedCountryShortKeys.has(c.shortKey));

  const countries = allCountries
    .map((c) => ({
      id: c.shortKey,
      value: translate(c.displayName),
      isSelectable: true,
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

  const getSubdivisionsForCountry = (countryShortKey: string): SubdivisionsData | undefined => {
    const country = allCountries.find((c) => c.shortKey === countryShortKey);
    if (!country?.subdivisions?.list?.length) {
      return undefined;
    }
    return {
      label: translate(country.subdivisions.displayName),
      options: country.subdivisions.list
        .map((subdivision) => ({
          id: `${countryShortKey}-${subdivision.key}`,
          value: translate(subdivision.displayName),
          isSelectable: true,
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    };
  };

  return {countries, getSubdivisionsForCountry};
}
